import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Menu, Sun, Moon } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";

const LOGO = "/logo.jpeg";

const navLinks = [
  { label: "Philosophy", href: "#philosophy" },
  { label: "Solutions", href: "#solutions" },
  { label: "Architecture", href: "#architecture" },
  { label: "Case Study", href: "#case-study" },
  { label: "Careers", href: "#careers" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleClick = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background/95 backdrop-blur-xl border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-24">
        <a href="#" className="flex items-center gap-3" data-testid="navbar-logo">
          <img src={LOGO} alt="Ojas AI" className="h-12 w-12 rounded-md" />
          <span className="font-heading font-bold text-xl text-foreground tracking-tight">
            OJAS <span className="text-[#c9a84c]">AI</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-10">
          {navLinks.map(({ label, href }) => (
            <button
              key={label}
              onClick={() => handleClick(href)}
              className="text-base font-heading font-medium text-foreground/70 hover:text-[#c9a84c] transition-colors duration-200"
              data-testid={`nav-link-${label.toLowerCase().replace(" ", "-")}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="text-muted-foreground hover:text-[#c9a84c]"
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          <Button
            onClick={() => handleClick("#contact")}
            className="hidden sm:inline-flex bg-[#c9a84c] text-[#0a1628] font-heading font-semibold hover:bg-[#c9a84c]/90 px-7 py-5 text-base"
            data-testid="navbar-demo-btn"
          >
            Request Demo
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5 text-foreground" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background border-l border-border w-72">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex flex-col gap-6 mt-12">
                {navLinks.map(({ label, href }) => (
                  <button
                    key={label}
                    onClick={() => handleClick(href)}
                    className="text-left text-lg font-body text-muted-foreground hover:text-[#c9a84c] transition-colors duration-200"
                  >
                    {label}
                  </button>
                ))}
                <Button
                  onClick={() => handleClick("#contact")}
                  className="bg-[#c9a84c] text-[#0a1628] font-heading font-semibold mt-4"
                >
                  Request Demo
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
